import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Wallet, Hash } from 'lucide-react';
import apiClient from '@/services/api';
import type { PaymentsReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { paymentClass } from '@/components/reports/paymentClass';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Custom Payment: non-cash, non-card tenders, mirroring the POS Custom Payment report. */
export default function CustomPaymentReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<PaymentsReport>({
    queryKey: ['payments-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getPaymentsReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const txns = (data?.transactions ?? []).filter((x) => paymentClass(x.paymentType, x.cardType) === 'other');
  const totalAmount = txns.reduce((a, x) => a + Number(x.amount), 0);
  const salesCount = txns.length;
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const fmtDate = (s: string | null) => (s ? format(parseISO(s), 'dd-MM-yyyy') : '—');
  // POS shows the custom tender name (CUSTOM_PAYMENT_NAME); fall back to the payment type.
  const payment = (x: PaymentsReport['transactions'][number]) => x.customPaymentName || x.paymentType || '—';

  const headers = ['Ticket ID', 'Payment', 'Date', 'Server', 'Total'];
  const rows = () => txns.map((x) => [
    x.posTicketId ?? '—',
    payment(x),
    fmtDate(x.processedAt),
    x.serverName ?? '—',
    formatCurrency(x.amount),
  ]);

  const sections = (): ReportSection[] => [{ heading: 'Transactions', headers, rows: rows() }];
  const kpis = (): [string, string][] => [
    ['Total Sales Count', salesCount.toLocaleString()],
    ['Total', formatCurrency(totalAmount)],
  ];

  return (
    <ReportFrame
      title="Custom Payment"
      description="Tenders that are neither cash nor card — gift cards, vouchers, on-account, etc."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={txns.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`custom-payment-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Custom Payment', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Custom Payment Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-2 gap-4">
        <KPICard label="Total Sales Count" value={salesCount.toLocaleString()} icon={Hash} palette="purple" />
        <KPICard label="Total" value={formatCurrency(totalAmount)} icon={Wallet} palette="blue" />
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Transactions{data?.transactionsTruncated ? ' (capped at 2000)' : ''}</h3>
        <SimpleTable headers={headers} rows={rows()} />
      </div></div>
    </ReportFrame>
  );
}
